import { Target, TrendingUp, Tent, Trophy, BarChart3, ClipboardList, CheckCircle } from 'lucide-react';
import type { Category } from '../../types';

// 保持旧版兼容的别名
type MainlineTaskType = Category;

// 总时长选项
export const TOTAL_DURATION_OPTIONS = [
  { label: '1个月', value: 30, Icon: Target },
  { label: '3个月', value: 90, Icon: TrendingUp },
  { label: '半年', value: 180, Icon: Tent },
  { label: '1年', value: 365, Icon: Trophy },
];

// 周期长度选项
export const CYCLE_LENGTH_OPTIONS = [
  { label: '10天', value: 10, description: '小步快跑' },
  { label: '15天', value: 15, description: '张弛有度' },
  { label: '30天', value: 30, description: '稳扎稳打' },
];

// 任务类型选项
export const TASK_TYPE_OPTIONS: Array<{
  type: MainlineTaskType;
  Icon: typeof BarChart3;
  label: string;
  description: string;
  examples: string;
  feature: string;
}> = [
  {
    type: 'NUMERIC',
    Icon: BarChart3,
    label: '数值型',
    description: '追踪可增可减的数值变化',
    examples: '减肥 10kg、攒钱 5000、还清贷款',
    feature: '记录起点→终点，见证每一步变化'
  },
  {
    type: 'CHECKLIST',
    Icon: ClipboardList,
    label: '清单型',
    description: '逐项完成一组待办事项',
    examples: '旅行清单、技能学习、项目里程碑',
    feature: '列出清单，逐个击破，掌控全局'
  },
  {
    type: 'CHECK_IN',
    Icon: CheckCircle,
    label: '打卡型',
    description: '累计时长、次数或数量的增长',
    examples: '冥想时长、跑步公里、写作字数',
    feature: '每次打卡都是积累，见证成长轨迹'
  }
];

// 打卡类型选项
export const CHECK_IN_TYPE_OPTIONS = [
  { value: 'TIMES' as const, label: '次数型', desc: '记录打卡次数' },
  { value: 'DURATION' as const, label: '时长型', desc: '记录时长' },
  { value: 'QUANTITY' as const, label: '数值型', desc: '记录数值' }
];

// 增减方向选项
export const DIRECTION_OPTIONS = [
  { value: 'INCREASE' as const, label: '增加' },
  { value: 'DECREASE' as const, label: '减少' }
];

// 清单模版
export interface ChecklistTemplate {
  id: string;
  title: string;
  items: string[];
}

export const CHECKLIST_TEMPLATES: ChecklistTemplate[] = [
  {
    id: 'life-100',
    title: '人生必做100件事',
    items: [
      '看一次极光', '来一次无计划的旅行', '勇敢告白一次', '写信给未来的自己', '去爱豆的演唱会',
      '学会游泳', '坐一次热气球', '在海边看日出', '拍一套写真', '给父母做一顿饭',
      '学一门乐器', '跳一次伞', '纹一个纹身', '养一只宠物', '种一棵树',
      '去沙漠看星空', '坐一次邮轮', '学会开车', '染一次头发', '参加一次马拉松',
      '在雨中漫步', '送一次外卖体验生活', '做一次志愿者', '学做一道拿手菜', '写一首诗',
      '在KTV唱到嗨', '看一场话剧', '坐一次过山车', '拍一部vlog', '和陌生人聊天'
    ]
  },
  {
    id: 'movie-top250',
    title: '电影 TOP250',
    items: [
      '肖申克的救赎', '霸王别姬', '阿甘正传', '泰坦尼克号', '这个杀手不太冷',
      '千与千寻', '辛德勒的名单', '盗梦空间', '忠犬八公', '楚门的世界',
      '海上钢琴师', '三傻大闹宝莱坞', '放牛班的春天', '机器人总动员', '触不可及',
      '当幸福来敲门', '怦然心动', '疯狂动物城', '指环王三部曲', '蝙蝠侠黑暗骑士',
      '星际穿越', '大话西游', '无间道', '熔炉', '飞屋环游记',
      '摔跤吧爸爸', '寻梦环游记', '让子弹飞', '天堂电影院', '教父'
    ]
  },
  {
    id: 'games',
    title: '此生必玩的游戏',
    items: [
      '荒野大镖客2', '黑神话：悟空', '塞尔达传说：王国之泪', '艾尔登法环', '博德之门3',
      '巫师3：狂猎', '战神：诸神黄昏', '只狼', '空洞骑士', '哈迪斯',
      'GTA5', '最后生还者', '底特律：变人', '对马岛之魂', '地平线：零之曙光',
      '生化危机4重制版', '死亡搁浅', '最终幻想7重制版', '赛博朋克2077', '双人成行',
      '传送门2', '黑暗之魂3', '血源诅咒', '塞尔达传说：旷野之息', '超级马里奥奥德赛',
      '动物森友会', '星露谷物语', '蔚蓝', '纪念碑谷', '植物大战僵尸'
    ]
  },
  {
    id: 'china-travel',
    title: '中国必去的50个地方',
    items: [
      '北京故宫', '上海外滩', '杭州西湖', '成都宽窄巷子', '西安兵马俑',
      '桂林山水', '张家界', '九寨沟', '黄山', '长城',
      '拉萨布达拉宫', '喀纳斯', '呼伦贝尔草原', '稻城亚丁', '丽江古城',
      '厦门鼓浪屿', '三亚亚龙湾', '青海湖', '敦煌莫高窟', '泰山',
      '苏州园林', '乌镇', '凤凰古城', '大理古城', '额济纳胡杨林',
      '长白山天池', '神农架', '婺源油菜花', '甘南草原', '阳朔西街'
    ]
  },
  {
    id: 'book-top250',
    title: '一生必读的书',
    items: [
      '红楼梦', '活着', '百年孤独', '1984', '小王子',
      '三体', '追风筝的人', '解忧杂货店', '围城', '平凡的世界',
      '白夜行', '挪威的森林', '月亮与六便士', '人类简史', '杀死一只知更鸟',
      '霍乱时期的爱情', '飘', '傲慢与偏见', '了不起的盖茨比', '呼啸山庄',
      '老人与海', '麦田里的守望者', '局外人', '简爱', '悲惨世界',
      '战争与和平', '罪与罚', '堂吉诃德', '浮生六记', '撒哈拉的故事'
    ]
  },
  {
    id: 'couple-100',
    title: '情侣必做100件事',
    items: [
      '一起看日出', '一起看日落', '海边散步', '去陌生城市旅行', '山顶看星星',
      '一起做一顿饭', '拍情侣写真', '交换礼物', '写情书给对方', '一起养宠物',
      '穿情侣装出门', '一起健身', '看演唱会', '去游乐园', '一起坐摩天轮',
      '雨中漫步', '一起放烟花', '牵手逛超市', '一起追剧', '玩双人游戏',
      '互相剪头发', '一起泡温泉', '野餐', '骑双人自行车', '一起学跳舞',
      '给对方唱歌', '互相画像', '一起看恐怖片', '去对方的城市', '见对方的朋友'
    ]
  },
  {
    id: 'ski-resorts',
    title: '全球滑雪胜地打卡',
    items: [
      '日本新雪谷', '日本白马', '日本志贺高原', '瑞士采尔马特', '瑞士圣莫里茨',
      '法国霞慕尼', '法国三峡谷', '奥地利圣安东', '加拿大惠斯勒', '美国阿斯彭',
      '美国杰克逊霍尔', '意大利多洛米蒂', '新西兰皇后镇', '韩国龙平', '中国崇礼',
      '中国亚布力', '中国长白山', '中国北大湖', '芬兰拉普兰', '挪威奥斯陆',
      '智利波蒂略', '阿根廷巴里洛切', '俄罗斯索契', '格鲁吉亚古多里', '安道尔',
      '西班牙内华达', '日本二世古', '日本富良野', '加拿大班夫', '美国帕克城'
    ]
  },
  {
    id: 'japanese-learning',
    title: '日语学习计划',
    items: [
      '学完五十音图', '掌握基础问候语', '背完N5核心词汇', '学会自我介绍', '能点餐购物',
      '看懂简单动漫台词', '学完N4语法', '背完N4核心词汇', '能进行日常对话', '学会敬语基础',
      '通过JLPT N5', '通过JLPT N4', '能看懂NHK简单新闻', '学完N3语法', '背完N3核心词汇',
      '能无字幕看日剧', '通过JLPT N3', '学完N2语法', '背完N2核心词汇', '能读日文小说',
      '通过JLPT N2', '学完N1语法', '背完N1核心词汇', '能听懂日语播客', '通过JLPT N1',
      '能进行商务日语对话', '能翻译日文文章', '能用日语写邮件', '能做日语口译', '达到母语水平'
    ]
  },
  {
    id: 'world-travel',
    title: '环球旅行愿望清单',
    items: [
      '巴黎埃菲尔铁塔', '伦敦大本钟', '罗马斗兽场', '希腊圣托里尼', '冰岛蓝湖',
      '挪威峡湾', '荷兰郁金香', '瑞士少女峰', '西班牙圣家堂', '葡萄牙里斯本',
      '纽约时代广场', '洛杉矶好莱坞', '旧金山金门大桥', '拉斯维加斯', '夏威夷',
      '东京浅草寺', '京都金阁寺', '大阪道顿堀', '首尔明洞', '新加坡滨海湾',
      '曼谷大皇宫', '巴厘岛', '马尔代夫', '迪拜哈利法塔', '土耳其卡帕多西亚',
      '埃及金字塔', '摩洛哥撒哈拉', '肯尼亚动物迁徙', '澳洲大堡礁', '新西兰霍比特村'
    ]
  },
  {
    id: 'romance-bucket',
    title: '浪漫爱情清单',
    items: [
      '在巴黎铁塔下亲吻', '在威尼斯坐贡多拉', '在马尔代夫看星空', '在北极圈看极光', '在樱花树下野餐',
      '在海边写下彼此名字', '交换手写情书', '一起看流星雨', '在烟花下拥抱', '录下彼此说我爱你',
      '穿婚纱拍照', '一起许愿', '互相给对方起昵称', '在餐厅定制专属菜品', '刻一对情侣戒指',
      '一起跨年倒数', '一起过每个纪念日', '给对方惊喜求婚', '一起选婚戒', '一起策划婚礼',
      '蜜月旅行', '一起装修新家', '养一只宠物', '一起学做对方爱吃的菜', '每年拍一张合照',
      '互相写年度总结', '一起看老照片', '录一段给未来的视频', '一起完成人生清单', '白头偕老'
    ]
  },
  {
    id: 'food-adventure',
    title: '吃遍中国美食',
    items: [
      '北京烤鸭', '天津狗不理包子', '兰州拉面', '西安肉夹馍', '重庆火锅',
      '成都串串香', '武汉热干面', '长沙臭豆腐', '广州早茶', '潮汕牛肉火锅',
      '云南过桥米线', '贵州酸汤鱼', '新疆大盘鸡', '内蒙烤全羊', '东北铁锅炖',
      '上海生煎包', '杭州东坡肉', '南京盐水鸭', '福建佛跳墙', '厦门沙茶面',
      '青岛啤酒配海鲜', '山西刀削面', '陕西羊肉泡馍', '四川麻婆豆腐', '湖南剁椒鱼头',
      '江西瓦罐汤', '河南烩面', '山东煎饼', '海南文昌鸡', '台湾卤肉饭'
    ]
  },
  {
    id: 'fitness-challenge',
    title: '健身运动挑战',
    items: [
      '连续跑步7天', '完成一次半马', '学会游泳', '连续健身30天', '做100个俯卧撑',
      '完成平板支撑5分钟', '学会一种舞蹈', '爬一座名山', '骑行100公里', '完成一次全马',
      '学会瑜伽基础', '跳绳1000个', '深蹲100个', '引体向上10个', '学会攀岩',
      '完成铁人三项', '学会滑板', '打一场网球', '打一场高尔夫', '学会拳击基础',
      '晨跑21天', '练出马甲线', '减脂10斤', '增肌5斤', '柔韧性达标',
      '学会倒立', '完成一次户外徒步', '参加运动比赛', '找到运动搭子', '养成运动习惯'
    ]
  },
  {
    id: 'anime-list',
    title: '经典动漫必看',
    items: [
      '进击的巨人', '鬼灭之刃', '咒术回战', '海贼王', '火影忍者',
      '死神', '全职猎人', '钢之炼金术师', '命运石之门', '未闻花名',
      '你的名字', '天气之子', '铃芽之旅', '千与千寻', '龙猫',
      '攻壳机动队', '新世纪福音战士', '叛逆的鲁路修', '魔法少女小圆', '四月是你的谎言',
      '排球少年', '灌篮高手', '钻石王牌', '黑子的篮球', '网球王子',
      '银魂', '关于我转生变成史莱姆这档事', '刀剑神域', 'Re:从零开始', '无职转生'
    ]
  },
  {
    id: 'museum-art',
    title: '博物馆艺术馆打卡',
    items: [
      '故宫博物院', '国家博物馆', '上海博物馆', '南京博物院', '陕西历史博物馆',
      '敦煌莫高窟', '三星堆博物馆', '中国美术馆', '龙美术馆', 'UCCA尤伦斯',
      '卢浮宫', '大英博物馆', '纽约大都会', '梵蒂冈博物馆', '乌菲兹美术馆',
      '普拉多博物馆', '阿姆斯特丹国立', '东京国立博物馆', '奥赛博物馆', '埃尔米塔什',
      '古根海姆', 'MoMA现代艺术', '蓬皮杜中心', '泰特现代美术馆', '芝加哥艺术博物馆',
      '梵高博物馆', '毕加索博物馆', '吉卜力美术馆', 'teamLab', '草间弥生美术馆'
    ]
  },
  {
    id: 'coffee-tea',
    title: '咖啡茶饮探店',
    items: [
      '尝试手冲咖啡', '品尝意式浓缩', '学会拉花', '打卡蓝瓶咖啡', '尝试冷萃咖啡',
      '喝一杯SOE单品', '体验咖啡工厂', '学习咖啡知识', '拥有自己的咖啡器具', '在咖啡馆工作一天',
      '品尝西湖龙井', '体验功夫茶', '尝试普洱茶', '打卡百年茶馆', '学习茶道',
      '品尝大红袍', '尝试抹茶', '打卡网红奶茶', '自己调一杯饮品', '收藏一套茶具',
      '参观茶园', '尝试调酒', '品尝威士忌', '尝试精酿啤酒', '打卡隐藏酒吧',
      '学习葡萄酒知识', '参观酒庄', '尝试日本清酒', '品尝中国黄酒', '收藏一瓶好酒'
    ]
  },
  {
    id: 'photography',
    title: '摄影技能提升',
    items: [
      '学会手动曝光', '掌握构图法则', '拍一组人像', '拍一组风光', '拍一组街拍',
      '学会后期修图', '拍摄日出日落', '拍摄星空银河', '拍摄延时摄影', '拍摄慢门流水',
      '学会闪光灯使用', '拍摄微距', '拍摄黑白照片', '参加摄影比赛', '办一次影展',
      '拍摄宠物', '拍摄美食', '拍摄建筑', '拍摄运动场景', '拍摄纪实故事',
      '学会无人机航拍', '拍一部短片', '学会视频剪辑', '积累1000张作品', '出版一本摄影集',
      '拍摄产品照', '拍摄婚礼', '拍摄新生儿', '尝试胶片摄影', '找到自己的风格'
    ]
  },
  {
    id: 'career-growth',
    title: '职场能力提升',
    items: [
      '学会PPT制作', '掌握Excel高级技巧', '提升演讲能力', '学会项目管理', '掌握时间管理',
      '学会思维导图', '提升写作能力', '学会数据分析', '掌握一门编程语言', '学会AI工具使用',
      '考取行业证书', '读完10本专业书', '参加行业大会', '建立人脉网络', '找到职业导师',
      '完成一次跳槽', '获得一次晋升', '带领一个团队', '完成一个大项目', '获得年度优秀',
      '学会向上管理', '提升情商沟通', '学会谈判技巧', '建立个人品牌', '开始副业尝试',
      '成为领域专家', '做一次公开分享', '培养接班人', '完成职业转型', '实现财务自由'
    ]
  },
  {
    id: 'cooking-skill',
    title: '厨艺修炼之路',
    items: [
      '学会煮饭', '学会炒青菜', '学会番茄炒蛋', '学会红烧肉', '学会糖醋排骨',
      '学会包饺子', '学会做蛋糕', '学会烤面包', '学会煲汤', '学会做披萨',
      '学会做寿司', '学会做咖喱', '学会做意面', '学会做火锅底料', '学会做卤味',
      '学会刀工', '学会调味', '学会摆盘', '学会食材搭配', '学会控制火候',
      '尝试分子料理', '学会做甜点', '学会做早午餐', '学会做减脂餐', '学会做宝宝辅食',
      '举办一次家宴', '教会别人一道菜', '创造自己的菜谱', '拍摄美食视频', '成为朋友圈大厨'
    ]
  },
  {
    id: 'self-improvement',
    title: '自我提升计划',
    items: [
      '养成早起习惯', '每天阅读30分钟', '学会冥想', '写日记100天', '断舍离整理',
      '学会理财', '建立应急基金', '学会说不', '克服拖延症', '提升专注力',
      '学一门外语', '学一门乐器', '学会画画', '学会书法', '学会下棋',
      '完成一次断网日', '尝试极简生活', '做一次公益', '原谅一个人', '和解过去',
      '写给未来的信', '制定五年计划', '找到人生导师', '定期复盘反思', '建立个人系统',
      '培养一个爱好', '交到知心好友', '学会表达感谢', '学会接受不完美', '活出真实自我'
    ]
  },
  {
    id: 'outdoor-adventure',
    title: '户外探险挑战',
    items: [
      '露营过夜', '徒步穿越', '攀登雪山', '穿越沙漠', '探索洞穴',
      '漂流探险', '滑翔伞', '蹦极跳', '潜水考证', '冲浪体验',
      '皮划艇', '帆船出海', '钓鱼垂钓', '观鸟摄影', '追逐日食',
      '看极光', '探访火山', '穿越雨林', '沙漠露营', '冰川徒步',
      '高空跳伞', '热气球', '直升机观光', '越野摩托', '房车旅行',
      '野外生存', '学会生火', '辨认野生植物', '观测星空', '记录自然笔记'
    ]
  }
];
